import React, { useMemo, useRef, useState, useCallback, useEffect } from "react";
import { Scatter } from "react-chartjs-2";
import { Chart as ChartJS, LinearScale, PointElement, LineElement, Tooltip, Legend, ChartData, ChartOptions } from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import { Box, Button, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { chartTheme } from "./chartTheme";

// Custom plugin to ensure canvas background matches chart card surface
const customCanvasBackgroundColor = {
  id: "customCanvasBackgroundColor",
  beforeDraw: (chart, args, options) => {
    const { ctx } = chart;
    ctx.save();
    ctx.globalCompositeOperation = "destination-over";
    ctx.fillStyle = options.color || chartTheme.surface;
    ctx.fillRect(0, 0, chart.width, chart.height);
    ctx.restore();
  },
};

// Update the interface to handle both structures
interface ChartDataProp {
  title: string;
  data: {
    datasets: {
      label: string;
      data: { x: number; y: number; label?: string }[];
    }[];
  };
  datasets: {
    label: string;
    data: { x: number; y: number; label?: string }[];
  }[];
  options: any;
  x_axis_key: string;
  y_axis_key: string;
}

// Define the component's props
interface ChartScatterProps {
  chartData: ChartDataProp;
}

// Helper function to check if the cursor is inside the main chart area (the grid)
const isInteractionInChartArea = ({ chart, point }) => {
  const canvas = chart.canvas;
  if (!chart.chartArea || !point) {
    canvas.style.touchAction = "auto";
    return false; // Cannot determine, so block to be safe
  }
  //chart.chartArea is the main chart area (the grid)
  const { top, bottom, left, right } = chart.chartArea;
  const { x, y } = point;

  const isInArea = x >= left && x <= right && y >= top && y <= bottom;

  // Dynamically set touch-action based on cursor position over the chart
  if (isInArea) {
    // When inside, let the zoom plugin handle gestures
    canvas.style.touchAction = "none";
  } else {
    // When outside, let the browser handle scrolling
    canvas.style.touchAction = "auto";
  }

  // This is the ONLY place where we want to allow zoom/pan.
  return isInArea;
};

ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, Legend, zoomPlugin);

const ChartScatter = ({ chartData }: ChartScatterProps) => {
  // console.log("chartData Scatter", chartData);
  // Defensive check to prevent rendering without data
  if (!chartData) {
    return null;
  }

  // Handle both data structures: direct and nested (specific for scatter data)
  const datasets = chartData.datasets || chartData.data?.datasets;

  // Handle different title locations
  const title = chartData.title || chartData.options?.plugins?.title?.text || "Chart";

  // Enhanced defensive check (adapted for scatter data)
  if (!datasets || !Array.isArray(datasets) || datasets.length === 0) {
    console.warn("ChartScatter: Missing or invalid datasets", { chartData });
    return null;
  }

  // Additional validation for scatter data structure
  const hasValidData = datasets.every(
    (dataset) =>
      dataset.data && Array.isArray(dataset.data) && dataset.data.every((point) => typeof point === "object" && typeof point.x === "number" && typeof point.y === "number")
  );

  if (!hasValidData) {
    console.warn("ChartScatter: Invalid scatter data structure", { datasets });
    return null;
  }

  const chartRef = useRef<ChartJS<"scatter">>(null);
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.between("xs", "lg"));
  // The logic for assigning colors by player name has been removed for simplicity.
  const [isZoomLocked, setIsZoomLocked] = useState(true); // NEW: State to control zoom lock

  // Reset zoom on double click or on the reset button
  const handleResetZoom = () => {
    if (chartRef.current) {
      chartRef.current.resetZoom();
    }
    // setIsZoomLocked(true); // Re-lock on reset
  };

  // NEW: Handler to toggle the zoom lock state
  const handleToggleZoomLock = useCallback(() => {
    setIsZoomLocked((prev) => !prev);
  }, []);

  // An expanded palette of colors to assign to each dataset.
  const colorPalette = [
    PureLightThemeCaaS.colors.alpha.orange[100],
    chartTheme.seriesMuted,
    "#E63946", // A strong red
    "#457B9D", // A muted blue
    "#52B788", // A calm green
    "#9D4EDD", // A vibrant purple
    "#FCA311", // A bright orange/yellow
    PureLightThemeCaaS.colors.alpha.gray[100], // Fallback color
    "#00A896", // Greenish-Cyan
    "#F07167", // Reddish-Coral
    "#A8DADC", // Light Blue/Cyan
    "#3D5A80", // Slate Blue
    "#FFB4A2", // Light Salmon/Peach
    "#8E9AAF", // Desaturated Blue/Gray
    "#5E2BFF", // Vibrant Blue/Purple
    "#F7B801", // Gold/Yellow
  ];

  // Fixed colors by position label (always the same color)
  // If a label is not listed here, we will fallback to the palette below.
  const positionColorMap: Record<string, string> = {
    QB: PureLightThemeCaaS.colors.alpha.orange[100],
    RB: chartTheme.seriesMuted,
    WR: "#E63946", // A strong red
    TE: "#457B9D", // A muted blue
    KE: "#52B788", // A calm green
    K: "#9D4EDD", // A vibrant purple
    DEF: "#FCA311", // A bright orange/yellow
    DST: "#00A896", // Greenish-Cyan
    FLEX: PureLightThemeCaaS.colors.alpha.gray[100], // Fallback color
  };

  // SOLUTION: Memoize data to prevent re-renders from resetting the chart state
  const dynamicData: ChartData<"scatter"> = useMemo(() => {
    return {
      datasets: datasets.map((dataset, index) => {
        // Assign a color from the palette based on the dataset's index.
        // This ensures that each label gets its own unique color.
        // const color = colorPalette[index % colorPalette.length];
        // Pick color by position label first; fallback to palette by index.
        const key = (dataset.label || "").toUpperCase().trim();
        const color = positionColorMap[key] ?? colorPalette[index % colorPalette.length];

        return {
          ...dataset,
          borderColor: color,
          backgroundColor: color,
          pointRadius: 6,
          pointHoverRadius: 8,
        };
      }),
    };
  }, [datasets]); // Use extracted datasets

  // SOLUTION: Memoize options to prevent re-renders from resetting the chart state
  const dynamicOptions: ChartOptions<"scatter"> = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          top: 5,
          right: 5,
          bottom: 5,
          left: 5,
        },
      },
      plugins: {
        tooltip: {
          callbacks: {
            // This callback removes the title from the tooltip.
            title: () => null,
            // This callback will show the player's name in the tooltip label.
            label: (context) => {
              // The 'raw' property contains the original data object for the point.
              const point = context.raw as { label?: string; x: number; y: number };
              const playerName = point.label || ""; // Player name from data point
              const xVal = context.parsed.x;
              const yVal = context.parsed.y;

              // Construct the new label with player name and coordinates.
              if (playerName && xVal !== null && yVal !== null) {
                return `${playerName}: (${xVal.toFixed(2)}, ${yVal.toFixed(2)})`;
              }

              // Fallback for data points without a label
              if (xVal !== null && yVal !== null) {
                return `(${xVal.toFixed(2)}, ${yVal.toFixed(2)})`;
              }

              return "";
            },
          },
        },
        legend: {
          position: "top" as const,
          align: "center" as const,
          labels: {
            color: chartTheme.muted,
            font: {
              // size: isMobileOrTablet ? 8 : 10,
              size: 10,
            },
            usePointStyle: true,
            pointStyle: "rectRounded",
            padding: 5,
          },
        },
        title: {
          display: false, // We will render the title manually
          text: title, // Use extracted title
          color: chartTheme.muted,
          font: {
            size: 16,
            weight: "bold",
          },
          padding: 5,
        },
        customCanvasBackgroundColor: {
          color: chartTheme.surface,
        },
        zoom: {
          pan: {
            enabled: !isZoomLocked,
            mode: "xy", // Pan on both axes for scatter plots
            threshold: 5,
            onPanStart: isInteractionInChartArea,
          },
          zoom: {
            wheel: {
              enabled: !isZoomLocked, // for mouse wheel zooming in desktop
            },
            pinch: {
              enabled: !isZoomLocked, // for pinch zooming in mobile
            },
            drag: {
              enabled: false, // This is the KEY to preventing drag-to-zoom conflicts.
            },
            mode: "xy", // Zoom on both axes for scatter plots
            // onZoomStart: isInteractionInChartArea,
          },
          limits: {
            x: {
              // Let chart.js determine min/max automatically from data
              minRange: 10,
            },
            y: {
              // Let chart.js determine min/max automatically from data
              minRange: 1,
            },
          },
        },
      },
      scales: {
        y: {
          type: "linear",
          ticks: {
            color: chartTheme.muted,
            font: {
              size: 12,
            },
            padding: 5,
          },
          grid: {
            color: chartTheme.grid,
          },
          title: {
            display: true,
            text: chartData.options?.scales?.y?.title?.text, // Use title from props options
            color: chartTheme.muted,
            font: {
              // size: isMobileOrTablet ? 8 : 14,
              size: 10,
              weight: "bold",
            },
            padding: 0,
          },
        },
        x: {
          type: "linear",
          ticks: {
            color: chartTheme.muted,
            font: {
              // size: isMobileOrTablet ? 10 : 16,
              size: 12,
            },
            padding: 5,
          },
          grid: {
            color: chartTheme.grid,
          },
          title: {
            display: true,
            text: chartData.options?.scales?.x?.title?.text, // Use title from props options
            color: chartTheme.muted,
            font: {
              // size: isMobileOrTablet ? 8 : 14,
              size: 10,
              weight: "bold",
            },
            padding: 0,
          },
        },
      },
    };
  }, [title, chartData.options]); // FIX: Remove isZoomLocked from dependency array

  // FIX: Effect to update zoom options directly on the chart instance without resetting zoom
  useEffect(() => {
    if (chartRef.current) {
      const chart = chartRef.current;
      chart.options.plugins.zoom.pan.enabled = !isZoomLocked;
      chart.options.plugins.zoom.zoom.wheel.enabled = !isZoomLocked;
      chart.options.plugins.zoom.zoom.pinch.enabled = !isZoomLocked;
      chart.update("none"); // Update chart without animation
    }
  }, [isZoomLocked]);

  // FIX: Effect to handle double-tap on touch devices
  const lastTap = useRef(0);
  useEffect(() => {
    const canvas = chartRef.current?.canvas;
    if (!canvas) return;

    const handleDoubleTap = (e: TouchEvent) => {
      const now = Date.now();
      if (now - lastTap.current <= 300) {
        handleToggleZoomLock();
        // Prevent the "double-tap to zoom page" on iOS
        e.preventDefault();
      }
      lastTap.current = now;
    };

    canvas.addEventListener("touchend", handleDoubleTap, { passive: false });

    return () => {
      canvas.removeEventListener("touchend", handleDoubleTap);
    };
  }, [handleToggleZoomLock]);

  // console.log("chartData Scatter", chartData);

  return (
    <Box
      onDoubleClick={handleToggleZoomLock}
      sx={{
        width: "90%",
        height: "450px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        margin: "0 auto",
        backgroundColor: chartTheme.surface,
        border: `1px solid ${chartTheme.edge}`,
        borderRadius: chartTheme.borderRadius,
        overflow: "hidden", // clip canvas so bottom corners stay rounded
      }}
    >
      {/* Custom Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", width: "100%" }}>
        <Box sx={{ width: "65px", display: "flex", justifyContent: "start" }}>
          {/* NEW: Zoom Lock/Unlock Button */}
          <Button onClick={handleToggleZoomLock} sx={{ p: 0, textTransform: "none", color: chartTheme.muted }}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Typography component="h3" sx={{ fontSize: "12px", fontWeight: "bold", pt: 0.5 }}>
                Zoom
              </Typography>
              {isZoomLocked ? <LockOutlinedIcon sx={{ fontSize: 18 }} /> : <LockOpenIcon sx={{ fontSize: 18 }} />}
            </Box>
          </Button>
        </Box>
        {/* Manual Title */}
        <Typography sx={{ flex: 1, color: chartTheme.ink, fontSize: "14px", fontWeight: "bold", textAlign: "center" }}>{title}</Typography>
        <Box sx={{ width: "65px", display: "flex", justifyContent: "end" }}>
          {/* Reset Button */}
          <Button onClick={handleResetZoom} sx={{ p: 0, textTransform: "none", color: chartTheme.ember }}>
            <Typography component="h3" sx={{ color: chartTheme.ember, fontSize: "12px", fontWeight: "bold", pt: 0.5 }}>
              Reset
            </Typography>
          </Button>
        </Box>
      </Box>

      {/* Chart component */}
      <Box sx={{ flexGrow: 1, position: "relative", width: "100%", height: "100%" }}>
        <Scatter ref={chartRef} options={dynamicOptions} data={dynamicData} plugins={[customCanvasBackgroundColor]} />
      </Box>
    </Box>
  );
};

export default React.memo(ChartScatter); // memo is used to prevent re-rendering of the component when the props are the same (applied this because a conflict with zoom in charts)
